import { useEffect, useCallback, useState } from 'react';
import {
    AttachmentIcon,
    DownloadIcon,
    ChevronRightIcon,
    InfoIcon,
    CloseIcon,
} from '@chakra-ui/icons';
import {
    Box,
    ListItem,
    Text,
    UnorderedList,
    Checkbox,
    HStack,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';

import { Button, Heading } from '@/components/v4';
import { CSVPrompt } from '@/pages/accounts/users/csv/CSVTemplateView';
import useCSVTemplate from './useCSVTemplate';

const walkthroughIds = {
    csv: {
        ufDownloadBtn: '/csvusers/button/download',
        ufUploadBtn: '/csvusers/button/upload',
        ufCloseBtn: '/csvusers/button/close',
    },
};

export default function CSVTemplateView({
    handleUploadData,
    downloadCSVText,
    header,
    step1Text: _step1Text,
    step2Text: _step2Text,
    info,
    instructionsHeader,
    instructionsBody,
    handleDownloadTemplate,
    loading,
    hasDangerouslySetInnerHTML = false,
    alertText = 'You are responsible for the changes you are about to make through this data import. Please ensure you have reviewed the instructions and understand the impact of your changes.',
    isPlanIdRequired = true,
    onBack,
}) {
    const { darkMode } = useSelector((state) => state.settings);
    const { planID } = useCSVTemplate();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { stateError } = useSelector((state) => state.modal);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [csvRowCount, setCsvRowCount] = useState(null);
    const [confirmationChecked, setConfirmationChecked] = useState(false);

    const handleBackButton = useCallback(() => {
        if (onBack) {
            onBack();
            return;
        }
        navigate('/plans');
        dispatch({
            type: 'RESET_HISTORY',
            payload: {
                pathname: '/plans',
            },
        });
    }, [onBack, dispatch, navigate]);

    // NOTE: in case of 401 error if user is still at CSV pages
    useEffect(() => {
        if (stateError?.status === 401) {
            handleBackButton();
        }
    }, [handleBackButton, stateError]);

    const handleConfirmationChange = (event) => {
        setConfirmationChecked(event.target.checked);
    };

    // Drag and drop logic
    const onDrop = (acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0 && confirmationChecked) {
            const file = acceptedFiles[0];
            setUploadedFile(file);
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    setCsvRowCount(results.data.length);
                },
            });
        }
    };

    const handleRemoveFile = (e) => {
        e.stopPropagation();
        setUploadedFile(null);
        setCsvRowCount(null);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'text/csv': ['.csv'] },
        multiple: false,
    });

    const handleValidate = () => {
        if (uploadedFile) {
            const event = { target: { files: [uploadedFile] } };
            handleUploadData(event);
        }
    };

    return (
        <Box display="flex" justifyContent="start" width="100%">
            <Box
                width="100%"
                maxWidth="700px"
                display="flex"
                flexDirection="column"
                gap="0"
                fontFamily="Inter"
                boxShadow="base"
                p="8"
                bg={darkMode ? 'dark.tcap' : 'white'}
                rounded="md">
                <Heading size="lg" mb={2}>
                    {header}
                </Heading>

                <Text mt="10px" fontSize="md" color="gray.600" mb={4}>
                    Download the template, review the rules, and upload your
                    file.
                </Text>

                {/* Important Information Alert */}
                <Box
                    p={4}
                    bg="blue.50"
                    borderRadius="md"
                    border="1px"
                    borderColor="blue.200"
                    borderLeft="5px solid"
                    borderLeftColor="blue.500"
                    color="blue.800"
                    mb={6}
                    mt={4}>
                    <HStack spacing={2} alignItems="flex-start">
                        <InfoIcon
                            color="blue.500"
                            boxSize={5}
                            mt={0.5}
                            flexShrink={0}
                        />
                        <Box>
                            <Text fontWeight="bold" mb={2}>
                                {info}
                            </Text>
                            <Text fontSize="sm">{alertText}</Text>
                        </Box>
                    </HStack>
                </Box>

                {/* Step 1 - Download Template */}
                <Box mt="25px" mb={6}>
                    <Text fontWeight="bold" mb={2}>
                        1. Download the CSV template
                    </Text>
                    <Button
                        isLoading={loading}
                        rightIcon={<DownloadIcon />}
                        sx={{ width: '220px' }}
                        size="sm"
                        onClick={handleDownloadTemplate}
                        data-walkthroughid={walkthroughIds.csv.ufDownloadBtn}>
                        {downloadCSVText}
                    </Button>
                </Box>

                {/* Step 2 - Confirm Before Upload */}
                <Box mb={6}>
                    <Text fontWeight="bold" mb={2}>
                        2. Confirm Before Upload
                    </Text>
                    <Checkbox
                        isChecked={confirmationChecked}
                        onChange={handleConfirmationChange}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            '& > span:first-of-type': {
                                marginTop: '4px',
                            },
                            '& > span:last-of-type': {
                                lineHeight: '1.5',
                            },
                        }}>
                        I confirm I am responsible for the changes made through
                        this data import including any cost incurred. I have
                        reviewed the instructions and understand the impact of
                        these changes.
                    </Checkbox>
                </Box>

                {/* Step 3 - Upload CSV */}
                <Box mb={6}>
                    <Text fontWeight="bold" mb={2}>
                        3. Upload CSV to be imported
                    </Text>
                    {uploadedFile ? (
                        <Box
                            border="2px solid"
                            borderColor="green.400"
                            borderRadius="md"
                            p={5}
                            bg="green.50"
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={2}>
                            <input {...getInputProps()} />
                            <HStack spacing={3}>
                                <AttachmentIcon boxSize={5} color="green.500" />
                                <Box>
                                    <Text
                                        color="green.700"
                                        fontWeight="semibold"
                                        fontSize="sm">
                                        {uploadedFile.name}
                                    </Text>
                                    <Text fontSize="xs" color="green.600">
                                        Valid CSV
                                        {csvRowCount !== null
                                            ? ` • ${csvRowCount} Rows`
                                            : ''}
                                    </Text>
                                </Box>
                            </HStack>
                            <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                leftIcon={<CloseIcon boxSize={2} />}
                                onClick={handleRemoveFile}>
                                Remove
                            </Button>
                        </Box>
                    ) : (
                        <Box
                            {...getRootProps()}
                            border="2px dashed"
                            borderColor={isDragActive ? 'blue.400' : 'gray.200'}
                            borderRadius="md"
                            p={8}
                            textAlign="center"
                            bg={isDragActive ? 'blue.50' : 'gray.50'}
                            cursor="pointer"
                            transition="border-color 0.2s, background 0.2s"
                            mb={2}>
                            <input {...getInputProps()} />
                            <AttachmentIcon
                                boxSize={10}
                                color="gray.400"
                                mb={2}
                            />
                            <Text
                                color="blue.600"
                                fontWeight="bold"
                                fontSize="md">
                                Click to upload{' '}
                                <span style={{ color: '#666' }}>
                                    or drag and drop
                                </span>
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                CSV files only
                            </Text>
                        </Box>
                    )}
                </Box>

                {/* Instructions */}
                <Box mb={6}>
                    <Text size="lg" fontWeight="bold" mb={2}>
                        {instructionsHeader}
                    </Text>
                    <UnorderedList>
                        {instructionsBody.map((v, i) =>
                            hasDangerouslySetInnerHTML ? (
                                <ListItem
                                    dangerouslySetInnerHTML={{
                                        __html: v,
                                    }}
                                    key={i}
                                />
                            ) : (
                                <ListItem key={i}>
                                    <CSVPrompt sentence={v} />
                                </ListItem>
                            ),
                        )}
                    </UnorderedList>
                </Box>

                {/* Navigation Buttons */}
                <Box display="flex" justifyContent="space-between" mt={4}>
                    <Button
                        variant="outline"
                        sx={{ width: '120px', bgColor: 'white' }}
                        size="sm"
                        _hover={{ opacity: 0.8 }}
                        onClick={handleBackButton}
                        data-walkthroughid={walkthroughIds.csv.ufCloseBtn}>
                        Back
                    </Button>
                    <Button
                        sx={{ width: '140px' }}
                        size="sm"
                        rightIcon={<ChevronRightIcon />}
                        onClick={handleValidate}
                        isDisabled={
                            !uploadedFile ||
                            (isPlanIdRequired && !planID) ||
                            !confirmationChecked
                        }>
                        Validate File
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
